/**
 * SectionServices — catálogo configurable de extras que se venden al huésped.
 *
 * No es un TPV de restaurante: un servicio es una ficha con precio, unidad de
 * cobro y antelación. La frase de cabecera es deliberada — el huésped verá
 * automáticamente lo que aquí esté activo, que es justo lo que demuestra el
 * principio de configurar una vez.
 */
import React, { useState } from "react";
import {
  ForkKnife, Car, Scooter, Gift, Flower, TShirt, Sparkle, Plus, Image as ImageIcon,
} from "@phosphor-icons/react";
import Chip from "@/components/ui-dv/Chip";
import SidePanel from "@/components/ui-dv/SidePanel";
import {
  Block, Card, FieldGrid, Field, TextArea, Select, Toggle, Note,
} from "@/pages/admin/pms/settings/SettingsPrimitives";
import { useSettingsV2, upsertService, toggleService } from "@/lib/settingsV2Store";
import { SERVICE_CATEGORIES, PRICING_UNITS } from "@/data/settingsV2Demo";

const ICONS = { ForkKnife, Car, Scooter, Gift, Flower, TShirt, Sparkle };

const PRICING_LABEL = Object.fromEntries(PRICING_UNITS.map((p) => [p.value, p.label]));
const CATEGORY_LABEL = Object.fromEntries(SERVICE_CATEGORIES.map((c) => [c.value, c.label]));

const EMPTY_DRAFT = {
  name: "",
  category: "other",
  description: "",
  cost_price_eur: 0,
  sale_price_eur: 0,
  tax_included: true,
  pricing_unit: "per_stay",
  supplier: "",
  lead_time_hours: 24,
  availability: "",
  active: true,
  image_set: false,
  icon: "Sparkle",
};

export default function SectionServices() {
  const { services } = useSettingsV2();
  const [draft, setDraft] = useState(null);

  const openNew = () => setDraft({ ...EMPTY_DRAFT });
  const openEdit = (service) => setDraft({ ...service });

  // Un servicio sin nombre no le sirve a nadie y ensucia el catálogo con una
  // tarjeta vacía: el guardado espera a que al menos tenga nombre.
  const canSave = !!draft && draft.name.trim().length > 0;

  const save = () => {
    if (!canSave) return;
    upsertService({ ...draft, name: draft.name.trim() });
    setDraft(null);
  };

  const activeCount = services.filter((s) => s.active).length;

  return (
    <div data-testid="section-services">
      <Block
        title="Servicios"
        description="Extras que el huésped puede añadir a su reserva."
        testId="services-catalog"
        action={
          <button
            type="button"
            onClick={openNew}
            data-testid="services-add"
            className="dv-btn dv-btn-outline text-xs min-h-[44px] inline-flex items-center gap-1.5"
          >
            <Plus size={14} weight="bold" />
            Añadir servicio
          </button>
        }
      >
        <Note testId="services-configure-once">
          Los servicios activos aparecerán automáticamente en el portal del huésped, en la ficha
          de reserva y en los informes de ingresos. Ahora mismo hay {activeCount} activos.
        </Note>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="services-grid">
          {services.map((s) => (
            <ServiceCard key={s.service_id} service={s} onEdit={() => openEdit(s)} />
          ))}
        </div>
      </Block>

      <SidePanel
        open={!!draft}
        onClose={() => setDraft(null)}
        title={draft?.service_id ? draft.name || "Servicio" : "Nuevo servicio"}
        subtitle="Servicio del catálogo"
        testId="service-panel"
        footer={
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setDraft(null)}
              className="dv-btn dv-btn-ghost text-sm min-h-[44px]"
              data-testid="service-cancel"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!canSave}
              title={canSave ? undefined : "Ponle un nombre al servicio"}
              className={`dv-btn dv-btn-primary text-sm min-h-[44px] ${canSave ? "" : "opacity-50 cursor-not-allowed"}`}
              data-testid="service-save"
            >
              Guardar
            </button>
          </div>
        }
      >
        {draft && (
          <div className="space-y-5">
            <FieldGrid cols={2}>
              <Field
                label="Nombre"
                value={draft.name}
                onChange={(v) => setDraft({ ...draft, name: v })}
                placeholder="Desayuno, traslado, clase de surf…"
                usedIn="services"
                testId="service-name"
              />
              <Select
                label="Categoría"
                value={draft.category}
                onChange={(v) => setDraft({ ...draft, category: v })}
                options={SERVICE_CATEGORIES}
                testId="service-category"
              />
              <TextArea
                label="Descripción"
                value={draft.description}
                onChange={(v) => setDraft({ ...draft, description: v })}
                rows={2}
                hint="La verá el huésped"
                testId="service-description"
              />
            </FieldGrid>

            <div className="pt-4 border-t border-stone-100 dark:border-stone-800">
              <div className="text-[11px] tracking-[0.18em] uppercase text-stone-500 mb-3">
                Precio
              </div>
              <FieldGrid cols={2}>
                <Field
                  label="Precio de coste"
                  type="number"
                  value={draft.cost_price_eur}
                  onChange={(v) => setDraft({ ...draft, cost_price_eur: v })}
                  suffix="€"
                  hint="Solo interno"
                  testId="service-cost"
                />
                <Field
                  label="Precio de venta"
                  type="number"
                  value={draft.sale_price_eur}
                  onChange={(v) => setDraft({ ...draft, sale_price_eur: v })}
                  suffix="€"
                  testId="service-price"
                />
                <Select
                  label="Unidad de cobro"
                  value={draft.pricing_unit}
                  onChange={(v) => setDraft({ ...draft, pricing_unit: v })}
                  options={PRICING_UNITS}
                  testId="service-pricing-unit"
                />
                <div className="flex items-end pb-1">
                  <Toggle
                    label="Impuesto incluido"
                    checked={draft.tax_included}
                    onChange={(v) => setDraft({ ...draft, tax_included: v })}
                    testId="service-tax"
                  />
                </div>
              </FieldGrid>
            </div>

            <div className="pt-4 border-t border-stone-100 dark:border-stone-800">
              <div className="text-[11px] tracking-[0.18em] uppercase text-stone-500 mb-3">
                Operativa
              </div>
              <FieldGrid cols={2}>
                <Field
                  label="Proveedor"
                  value={draft.supplier}
                  onChange={(v) => setDraft({ ...draft, supplier: v })}
                  placeholder="Interno o empresa externa"
                  testId="service-supplier"
                />
                <Field
                  label="Antelación necesaria"
                  type="number"
                  value={draft.lead_time_hours}
                  onChange={(v) => setDraft({ ...draft, lead_time_hours: v })}
                  suffix="h"
                  testId="service-lead-time"
                />
                <Field
                  label="Disponibilidad"
                  value={draft.availability}
                  onChange={(v) => setDraft({ ...draft, availability: v })}
                  placeholder="Todos los días · 08:00–20:00"
                  span={2}
                  testId="service-availability"
                />
              </FieldGrid>
            </div>

            <div className="pt-4 border-t border-stone-100 dark:border-stone-800 space-y-1">
              <Toggle
                label="Imagen configurada"
                description="En la vista previa la imagen es un marcador de posición."
                checked={draft.image_set}
                onChange={(v) => setDraft({ ...draft, image_set: v })}
                testId="service-image"
              />
              <Toggle
                label="Servicio activo"
                description="Solo los servicios activos se ofrecen al huésped."
                checked={draft.active}
                onChange={(v) => setDraft({ ...draft, active: v })}
                testId="service-active"
              />
            </div>
          </div>
        )}
      </SidePanel>
    </div>
  );
}

function ServiceCard({ service, onEdit }) {
  const Icon = ICONS[service.icon] || Sparkle;
  return (
    <div
      className={`dv-card flex flex-col ${service.active ? "" : "opacity-70"}`}
      data-testid={`service-card-${service.service_id}`}
    >
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-lg bg-stone-100 dark:bg-stone-800 flex items-center justify-center shrink-0">
          <Icon size={18} className="text-stone-600 dark:text-stone-300" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-stone-900 dark:text-stone-100 truncate">{service.name}</div>
          <div className="text-[11px] text-stone-400">{CATEGORY_LABEL[service.category]}</div>
        </div>
        {!service.image_set && (
          <span title="Sin imagen" data-testid={`service-no-image-${service.service_id}`}>
            <ImageIcon size={14} className="text-stone-300 dark:text-stone-600 shrink-0" />
          </span>
        )}
      </div>

      <p className="text-xs text-stone-500 dark:text-stone-400 mt-3 line-clamp-2 flex-1">
        {service.description}
      </p>

      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-lg text-stone-900 dark:text-stone-100 tabular-nums">
          {service.sale_price_eur} €
        </span>
        <span className="text-[11px] text-stone-400">
          {PRICING_LABEL[service.pricing_unit]}
        </span>
      </div>

      <div className="mt-1 text-[11px] text-stone-400">
        Antelación {service.lead_time_hours} h
        {service.supplier ? ` · ${service.supplier}` : ""}
      </div>

      <div className="mt-4 pt-3 border-t border-stone-100 dark:border-stone-800 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => toggleService(service.service_id)}
          data-testid={`service-toggle-${service.service_id}`}
          aria-pressed={service.active}
          className="inline-flex items-center min-h-[44px]"
        >
          <Chip tone={service.active ? "emerald" : "stone"} size="xs" dot>
            {service.active ? "Activo" : "Inactivo"}
          </Chip>
        </button>
        <button
          type="button"
          onClick={onEdit}
          data-testid={`service-edit-${service.service_id}`}
          className="dv-btn dv-btn-ghost text-xs min-h-[44px]"
        >
          Editar
        </button>
      </div>
    </div>
  );
}
